import {
    ISO2768_ANGULAR_SHORTER_SIDE_INCLUSIVE_UPPER_MM,
    ISO2768_LINEAR_INCLUSIVE_UPPER_MM,
    ISO2768_RADIUS_CHAMFER_INCLUSIVE_UPPER_MM,
    toleranceTableColumnIndex
} from '../utils/metrologyToleranceColumn';

/**
 * ISO 2768-1 general tolerances for linear dimensions (mm).
 * Size ranges: lower bound exclusive, upper bound inclusive.
 * Nominal above 4000 mm uses the last table column (ISO 2768-1 table ends at 4000 mm).
 */

// ISO 2768-1 angular tolerances (degrees). Based on shorter side length in mm.
// Ranges: up to 10, 10-50, 50-120, 120-400, over 400 mm.
const ANGULAR_TOLERANCES: Record<string, number[]> = {
    f: [1, 0.5, 0.333, 0.167, 0.083],
    m: [1, 0.5, 0.333, 0.167, 0.083],
    c: [1.5, 1, 0.5, 0.25, 0.167],
    v: [3, 2, 1, 0.5, 0.333]
};

function getAngularSizeRangeIndex(shorterSideMm: number): number {
    return toleranceTableColumnIndex(
        shorterSideMm,
        ISO2768_ANGULAR_SHORTER_SIDE_INCLUSIVE_UPPER_MM,
        'append-open-ended-column'
    );
}

const TOLERANCES: Record<string, (number | null)[]> = {
    f: [0.05, 0.05, 0.1, 0.15, 0.2, 0.3, 0.5, null],
    m: [0.1, 0.1, 0.2, 0.3, 0.5, 0.8, 1.2, 2],
    c: [0.2, 0.3, 0.5, 0.8, 1.2, 2, 3, 4],
    v: [null, 0.5, 1, 1.5, 2.5, 4, 6, 8]
};

/** ISO 2768-1 Table 2 — broken edges (external radii, chamfer heights), same column count as ranges above. */
const TOLERANCES_RADIUS_CHAMFER: Record<string, (number | null)[]> = {
    f: [0.2, 0.5, 1.0],
    m: [0.2, 0.5, 1.0],
    c: [0.4, 1.0, 2.0],
    v: [0.4, 1.0, 2.0]
};

function getSizeRangeIndex(nominalMm: number): number {
    return toleranceTableColumnIndex(
        nominalMm,
        ISO2768_LINEAR_INCLUSIVE_UPPER_MM,
        'clamp-to-last-edge-column'
    );
}

function getRadiusChamferSizeRangeIndex(nominalMm: number): number {
    // Table 2 has three bands (edges [3, 6]); nominal strictly above the last edge uses the third column.
    // clamp-to-last-edge-column would map d>6 to index 1 (wrong — e.g. R15 mm would get ±0.5 instead of ±1 for class m).
    return toleranceTableColumnIndex(
        nominalMm,
        ISO2768_RADIUS_CHAMFER_INCLUSIVE_UPPER_MM,
        'append-open-ended-column'
    );
}

export const ISO2768_DROPDOWN_OPTIONS = [ 'ISO 2768 - f', 'ISO 2768 - m', 'ISO 2768 - c', 'ISO 2768 - v', 'ISO 22081' ] as const;

export const ISO2768_2_DROPDOWN_OPTIONS = [ 'ISO 2768-2 - H', 'ISO 2768-2 - K', 'ISO 2768-2 - L' ] as const;

export const ISO2768_2_DEFAULT = 'ISO 2768-2 - K';

export const SHAPE_STANDARD_DROPDOWN_OPTIONS = [
    'ISO 2768-2 - H',
    'ISO 2768-2 - K',
    'ISO 2768-2 - L',
    'ISO 22081'
] as const;

export const SHAPE_STANDARD_DEFAULT = 'ISO 2768-2 - K';

export const ISO2768_DEFAULT = 'ISO 2768 - m';

export function extractIso2768Class(standardStr: string): 'f' | 'm' | 'c' | 'v' | null {
    const s = (standardStr || '').trim().toLowerCase();
    const m = s.match(/iso\s*2768\s*[-]?\s*([fmcv])/i) || s.match(/iso2768\s*[-]?\s*([fmcv])/i);
    if (m) {
        const cls = m[1] as 'f' | 'm' | 'c' | 'v';
        if (TOLERANCES[cls]) return cls;
    }
    if (/iso\s*2768|iso2768/i.test(s)) return 'm';
    return null;
}

export function lookupIso2768Tolerance(
    standardStr: string,
    nominalMm: number
): { tolerancePlus: string; toleranceMinus: string } | null {
    const cls = extractIso2768Class(standardStr);
    if (!cls) return null;
    const idx = getSizeRangeIndex(nominalMm);
    const val = TOLERANCES[cls][idx];
    if (val === null) return null;
    const s = val.toFixed(2).replace(/\.?0+$/, '') || '0';
    return { tolerancePlus: s, toleranceMinus: s };
}

/** ISO 2768-1 Table 2 — general tolerances for external radii and chamfer heights (nominal = radius / height in mm). */
export function lookupIso2768RadiusTolerance(
    standardStr: string,
    nominalMm: number
): { tolerancePlus: string; toleranceMinus: string } | null {
    const cls = extractIso2768Class(standardStr);
    if (!cls) return null;
    const idx = getRadiusChamferSizeRangeIndex(nominalMm);
    const val = TOLERANCES_RADIUS_CHAMFER[cls][idx];
    if (val === null) return null;
    const s = val.toFixed(2).replace(/\.?0+$/, '') || '0';
    return { tolerancePlus: s, toleranceMinus: s };
}

/** Same table as {@link lookupIso2768RadiusTolerance} (ISO 2768-1 Table 2 — chamfer heights). */
export const lookupIso2768ChamferHeightTolerance = lookupIso2768RadiusTolerance;

/** Optional drawing/editor prefix for chamfer (Table 2) before the "N x α" callout. */
export function stripIso2768ChamferClassPrefix(s: string): string {
    return (s || '').trim().replace(/^\s*[Cc]\s*/i, '').trim();
}

/** Same rules as OCR numeric tolerance prefix (±, +, -, decimals, EU ",05"). */
export function iso2768ChamferRestLooksLikeNumericTolerance(tolerancePart: string): boolean {
    const s = tolerancePart.trim();
    if (!s) return false;
    if (s.startsWith('±')) return true;
    if (s.startsWith('+') || s.startsWith('-')) return true;
    if (s.startsWith('.')) return true;
    if (s.startsWith('0')) return true;
    if (/^,\d/.test(s)) return true;
    return false;
}

const CHAMFER_CORE_DISPLAY_RE =
    /^(\d+(?:[.,]\d+)?)\s*[xXхХ×Ｘ]\s*(\d+(?:[.,]\d+)?)\s*(?:([°º˚∘′]|deg\b|grad\b))?\s*/iu;

/**
 * Chamfer callout "N×α°" / "N x α" (ISO 2768-1 Table 2 uses height N in mm).
 * Returns the height leg string (first number) or null.
 * OCR may append a label (e.g. "Chamfer") or a numeric tolerance (e.g. "±0.1") after the angle.
 * Also accepts editor shorthand "C" + height only (e.g. "C2", "c 2,5") when C prefix is present.
 */
export function parseIso2768ChamferHeightLegString(trimmed: string): string | null {
    const raw = (trimmed || '').trim();
    const hadCprefix = /^\s*[Cc]\s*\S/i.test(raw);
    const t = stripIso2768ChamferClassPrefix(raw);
    const m = t.match(CHAMFER_CORE_DISPLAY_RE);
    if (m && m.index === 0) {
        const angleStr = m[2].replace(',', '.');
        const angleDeg = parseFloat(angleStr);
        if (Number.isNaN(angleDeg)) return null;
        const rest = t.slice(m[0].length).trim();
        if (rest) {
            if (!iso2768ChamferRestLooksLikeNumericTolerance(rest)) {
                if (/[±]/.test(rest)) return null;
                if (/^\d/.test(rest)) return null;
            }
        }
        const hasAngleSuffix = !!(m[3] && String(m[3]).trim());
        if (hasAngleSuffix) return m[1];
        const typicalChamferAnglesDeg = [15, 22.5, 30, 37.5, 45, 52.5, 60];
        const matchesTypicalAngle = typicalChamferAnglesDeg.some(a => Math.abs(a - angleDeg) < 1e-6);
        if (!matchesTypicalAngle) return null;
        return m[1];
    }
    if (hadCprefix) {
        const sh = t.match(/^(\d+(?:[.,]\d+)?)\s*$/);
        if (sh) return sh[1];
    }
    return null;
}

/**
 * Editor nominal for a recognized chamfer: "C" + height leg only (Table 2), no "× angle" in the field.
 */
export function getIso2768ChamferNominalDisplayCore(s: string): string | null {
    const leg = parseIso2768ChamferHeightLegString((s || '').trim());
    if (leg === null) return null;
    return `C${leg}`;
}

/** Text after the chamfer display core (e.g. "±0.1"); empty if none or not a chamfer line. */
export function getIso2768ChamferToleranceSuffixAfterCore(trimmed: string): string {
    if (parseIso2768ChamferHeightLegString(trimmed) === null) return '';
    const body = stripIso2768ChamferClassPrefix(trimmed);
    const m = body.match(CHAMFER_CORE_DISPLAY_RE);
    if (!m || m.index !== 0) return '';
    return body.slice(m[0].length).trim();
}

export function isIso2768ChamferHeightCallout(trimmed: string): boolean {
    return parseIso2768ChamferHeightLegString(trimmed) !== null;
}

export function getIso2768ChamferHeightLegMm(trimmed: string): number | null {
    const leg = parseIso2768ChamferHeightLegString(trimmed);
    if (!leg) return null;
    const v = parseFloat(leg.replace(',', '.'));
    return Number.isFinite(v) && v >= 0 ? v : null;
}

export function lookupIso2768AngularTolerance(
    standardStr: string,
    shorterSideMm?: number
): { tolerancePlus: string; toleranceMinus: string } | null {
    const cls = extractIso2768Class(standardStr);
    if (!cls) return null;
    const idx = shorterSideMm != null ? getAngularSizeRangeIndex(shorterSideMm) : 0;
    const val = ANGULAR_TOLERANCES[cls][idx];
    const s = val.toFixed(3).replace(/\.?0+$/, '') || '0';
    return { tolerancePlus: s, toleranceMinus: s };
}

/** Parsed ISO / ISO 2768 / ISO 22081 hints from a single OCR string (editor region). */
export interface IsoOcrHints {
    hasIsoWord: boolean;
    mentions2768: boolean;
    mentions22081: boolean;
    /** ISO 2768-1 linear class letters found after "ISO 2768". */
    linearClassLetters: ('f' | 'm' | 'c' | 'v')[];
    /** ISO 2768-2 geometrical class letters found after "ISO 2768". */
    shapeClassLetters: ('H' | 'K' | 'L')[];
}

/**
 * Requires the ISO word (or iso2768 / iso22081 token) and a 2768 or 22081 reference.
 * Collects class letters for linear (f/m/c/v) and shape (H/K/L).
 */
export function parseIsoOcrHints(raw: string): IsoOcrHints | null {
    const sentence = (raw || '').trim();
    if (!sentence) return null;
    const referencesBare22081 = /(?:^|[^\d.])22081(?!\d)/.test(sentence);
    const hasIsoWord =
        /\biso\b/i.test(sentence) ||
        /\biso2768\b/i.test(sentence) ||
        /\biso22081\b/i.test(sentence) ||
        referencesBare22081;
    if (!hasIsoWord) return null;

    const mentions22081 =
        /\biso\s*22081\b/i.test(sentence) ||
        /\biso22081\b/i.test(sentence) ||
        referencesBare22081;
    const mentions2768 =
        /\biso\s*2768\b/i.test(sentence) ||
        /\biso2768\b/i.test(sentence) ||
        /\b2768\b/.test(sentence);

    if (!mentions2768 && !mentions22081) return null;

    const linearClassLetters: ('f' | 'm' | 'c' | 'v')[] = [];
    const shapeClassLetters: ('H' | 'K' | 'L')[] = [];

    /** OCR often glues linear + shape class (e.g. "-mK"); \b after first letter would fail — scan the tail. */
    const iso2768Head = /(?:\biso\s*2768\b|\biso2768\b)(?:\s*[-]?\s*2)?/gi;
    for (const m of sentence.matchAll(iso2768Head)) {
        const tail = sentence.slice(m.index! + m[0].length);
        let i = 0;
        while (i < tail.length) {
            const unit = tail[i];
            const c = unit.toLowerCase();
            if (/\s|[-–.:]/.test(unit)) {
                i++;
                continue;
            }
            if ('fmcv'.includes(c)) {
                const L = c as 'f' | 'm' | 'c' | 'v';
                if (!linearClassLetters.includes(L)) linearClassLetters.push(L);
                i++;
            } else if ('hkl'.includes(c)) {
                const u = c.toUpperCase() as 'H' | 'K' | 'L';
                if (!shapeClassLetters.includes(u)) shapeClassLetters.push(u);
                i++;
            } else {
                break;
            }
        }
    }

    return {
        hasIsoWord,
        mentions2768,
        mentions22081,
        linearClassLetters,
        shapeClassLetters
    };
}

const LINEAR_MENU_OPTIONS = ISO2768_DROPDOWN_OPTIONS as readonly string[];
const SHAPE_MENU_OPTIONS = SHAPE_STANDARD_DROPDOWN_OPTIONS as readonly string[];

/**
 * Pull ISO designation fragments from a note block (e.g. "ISO 2768:1989 - cl. -0.5", "ISO 13715:2000").
 * Omits surrounding phrases like "acc. to" / TPS references.
 */
export function extractIsoStandardSnippetsFromRegionText(text: string): string[] {
    const s = (text || '').trim();
    if (!s) return [];

    const re =
        /\bISO(?:\s*\/[A-Z]{2,})?\s+\d{3,5}(?:[-–]\d)?(?::\d{4})?(?:(?:\s*[-–]\s*(?:cl\.?\s*)*[-+]?\d+(?:[.,]\d+)?)|(?:\s*[-–]\s*[fmcvHKL]))?/gi;

    const seen = new Set<string>();
    const out: string[] = [];
    for (const m of s.matchAll(re)) {
        const raw = m[0].replace(/\s+/g, ' ').trim();
        if (!raw) continue;
        const key = raw.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(raw);
    }
    return out;
}

/**
 * Build "Recognized standard" lines from the editor OCR region text only (not from menu selection).
 * Combines dropdown-style labels from parsed class letters with ISO+number snippets from the raw text.
 */
export function getStandardLabelsRecognizedFromRegionOcr(
    hints: IsoOcrHints | null,
    regionTextTrimmed: string
): string[] {
    const out: string[] = [];
    const add = (x: string) => {
        if (!x) return;
        if (!out.some(y => y.trim().toLowerCase() === x.trim().toLowerCase())) out.push(x);
    };

    if (hints) {
        if (hints.mentions22081) {
            add('ISO 22081');
        }

        if (hints.mentions2768) {
            for (const ch of hints.linearClassLetters) {
                const label = `ISO 2768 - ${ch}`;
                if (LINEAR_MENU_OPTIONS.includes(label)) add(label);
            }
            for (const L of hints.shapeClassLetters) {
                const label = `ISO 2768-2 - ${L}`;
                if (SHAPE_MENU_OPTIONS.includes(label)) add(label);
            }
        }
    }

    for (const snippet of extractIsoStandardSnippetsFromRegionText(regionTextTrimmed)) {
        add(snippet);
    }

    return out;
}

/**
 * Map region OCR hints to editor dropdown values (last letter wins if several were read).
 */
export function getMenuSelectionsFromIsoOcrHints(hints: IsoOcrHints): {
    linear: string | null;
    shape: string | null;
} {
    let linear: string | null = null;
    let shape: string | null = null;

    const lastLin =
        hints.linearClassLetters.length > 0
            ? hints.linearClassLetters[hints.linearClassLetters.length - 1]
            : null;
    const lastSh =
        hints.shapeClassLetters.length > 0
            ? hints.shapeClassLetters[hints.shapeClassLetters.length - 1]
            : null;

    if (lastLin) {
        const label = `ISO 2768 - ${lastLin}`;
        if (LINEAR_MENU_OPTIONS.includes(label)) linear = label;
    }
    if (lastSh) {
        const label = `ISO 2768-2 - ${lastSh}`;
        if (SHAPE_MENU_OPTIONS.includes(label)) shape = label;
    }

    if (hints.mentions22081 && !lastLin) {
        linear = 'ISO 22081';
    }

    return { linear, shape };
}

/**
 * Whether parsed OCR ISO hints allow treating tolerances as coming from the active linear general standard.
 */
export function ocrLinearHintsAllowStandardTolerance(
    hints: IsoOcrHints | null,
    effectiveGeneralStandard: string | null
): boolean {
    if (!hints || !effectiveGeneralStandard) return true;

    if (effectiveGeneralStandard === 'ISO 22081') {
        if (hints.mentions22081) return true;
        if (hints.mentions2768) return false;
        return true;
    }

    if (!hints.mentions2768) {
        if (hints.mentions22081) return false;
        return true;
    }

    const letters = hints.linearClassLetters;
    if (letters.length === 0) return true;
    const cls = extractIso2768Class(effectiveGeneralStandard);
    if (!cls) return false;
    return letters.includes(cls);
}
